import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  CircularProgress,
  Chip,
  Alert,
  Typography,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

interface ClassGroup {
  id: string;
  name: string;
}

interface ClassModel {
  id: string;
  name: string;
}

interface SectionModel {
  id: string;
  name: string;
  sequenceOrder: number;
  isActive: boolean;
  classId: string;
  class?: ClassModel;
  _count?: {
    students: number;
  };
}

const SectionMaster: React.FC = () => {
  const [sections, setSections] = useState<SectionModel[]>([]);
  const [classGroups, setClassGroups] = useState<ClassGroup[]>([]);
  const [classes, setClasses] = useState<ClassModel[]>([]);
  
  // Filters
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    sequenceOrder: 0,
    classId: '',
    isActive: true,
  });

  const [error, setError] = useState<string | null>(null);

  // Initial Load: Get Class Groups
  useEffect(() => {
    fetchClassGroups();
  }, []);

  // When Group changes, fetch Classes
  useEffect(() => {
    if (selectedGroupId) {
      fetchClasses(selectedGroupId);
    } else {
      setClasses([]);
      setSelectedClassId('');
    }
  }, [selectedGroupId]);

  // When Class changes, fetch Sections
  useEffect(() => {
    if (selectedClassId) {
      fetchSections(selectedClassId);
    } else {
      setSections([]);
    }
  }, [selectedClassId]);

  const fetchClassGroups = async () => {
    try {
      const res = await axios.get('/system-settings/class-group?isActive=true');
      setClassGroups(res.data);
    } catch (err) {
      console.error('Failed to fetch class groups', err);
    }
  };

  const fetchClasses = async (groupId: string) => {
    try {
      const res = await axios.get('/system-settings/classes', { params: { classGroupId: groupId } });
      setClasses(res.data.filter((c: any) => c.isActive)); // Only active classes
    } catch (err) {
      console.error('Failed to fetch classes', err);
    }
  };

  const fetchSections = async (classId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/system-settings/section', { params: { classId } });
      setSections(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch sections');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({
      name: '',
      sequenceOrder: sections.length + 1,
      classId: selectedClassId || '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (sec: SectionModel) => {
    setEditingId(sec.id);
    setFormData({
      name: sec.name,
      sequenceOrder: sec.sequenceOrder,
      classId: sec.classId,
      isActive: sec.isActive,
    });
    // Ensure we have the class list for the section's class if editing from a different view
    // But here we enforce filtering by class first, so classes should be loaded.
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    try {
      await axios.delete(`/system-settings/section/${id}`);
      if (selectedClassId) fetchSections(selectedClassId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete section');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/section/${id}/toggle-status`);
      setSections((prev) =>
        prev.map((sec) =>
          sec.id === id ? { ...sec, isActive: !currentStatus } : sec
        )
      );
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to toggle status');
      if (selectedClassId) fetchSections(selectedClassId);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        sequenceOrder: Number(formData.sequenceOrder),
      };

      if (editingId) {
        await axios.patch(`/system-settings/section/${editingId}`, payload);
      } else {
        await axios.post('/system-settings/section', payload);
      }
      setDialogOpen(false);
      if (selectedClassId) fetchSections(selectedClassId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save section');
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Section Master</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
                select
                label="Class Group"
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                sx={{ minWidth: 180 }}
                size="small"
            >
                <MenuItem value="">Select Group</MenuItem>
                {classGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                        {group.name}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                select
                label="Class"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                sx={{ minWidth: 180 }}
                size="small"
                disabled={!selectedGroupId}
            >
                <MenuItem value="">Select Class</MenuItem>
                {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                        {cls.name}
                    </MenuItem>
                ))}
            </TextField>
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={handleCreate}
                disabled={!selectedClassId}
            >
                Add Section
            </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sequence</TableCell>
              <TableCell>Section Name</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Students</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow>
                    <TableCell colSpan={6} align="center"><CircularProgress /></TableCell>
                </TableRow>
            ) : sections.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={6} align="center">
                        {selectedClassId ? 'No sections found.' : 'Please select a Class to view sections.'}
                    </TableCell>
                </TableRow>
            ) : (
                sections.map((sec) => (
                  <TableRow key={sec.id}>
                    <TableCell>{sec.sequenceOrder}</TableCell>
                    <TableCell>{sec.name}</TableCell>
                    <TableCell>{sec.class?.name}</TableCell>
                    <TableCell>{sec._count?.students || 0}</TableCell>
                    <TableCell>
                      <Switch
                        checked={sec.isActive}
                        onChange={() => handleToggleStatus(sec.id, sec.isActive)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEdit(sec)}><EditIcon /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(sec.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editingId ? 'Edit Section' : 'New Section'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 300 }}>
            {/* We show Class dropdown here but it might be read-only or restricted to the selected class to avoid confusion */}
             <TextField
                select
                label="Class"
                value={formData.classId}
                onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                fullWidth
                required
                disabled // Lock to selected class for simplicity, or allow changing? User req: "Class (dropdown)".
                // If I allow changing, I need to make sure the dropdown has options. The options are in `classes` state.
                // But `classes` state depends on `selectedGroupId`. If I want to move section to another class in another group, it's complex.
                // For now, I'll enable it but it will only show classes of CURRENT group.
            >
                {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                        {cls.name}
                    </MenuItem>
                ))}
            </TextField>

            <TextField
                label="Section Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
            />
            
            <TextField
                label="Sequence Order"
                type="number"
                value={formData.sequenceOrder}
                onChange={(e) => setFormData({ ...formData, sequenceOrder: Number(e.target.value) })}
                fullWidth
                required
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                }
                label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SectionMaster;
